/* The Profiles screen's readings: everything it decides about a table or a column that is not layout.
 *
 * Pure functions over the wire shapes, so each rule is testable without a screen and the screen holds
 * no thresholds of its own. The readings a column shares with the dataset page — its share format, its
 * dominance rule and its drift verdict — come from `profiler`, so a column means the same thing on
 * both screens.
 *
 * **Two thresholds here are design proposals, not product decisions**, and are named so they can be
 * found and changed in one place: the freshness bands (24 hours and 7 days) and the 70% line below
 * which a completeness reads as at risk. The product's only completeness threshold is the 90% watch
 * line.
 */

import { cardinalityLevel } from '@/features/profiler';
import type { CardinalityLevel } from '@/features/profiler';
import type {
  BaselineFinding,
  ColumnDrift,
  ColumnSketch,
  DatasetFacts,
  ProfileBaseline,
} from '@/shared/api/generated/catalog';

/* ------------------------------------------------------------------ freshness */

export type Freshness = 'fresh' | 'ageing' | 'stale' | 'never';

/** The order the estate reads in: the tables that most need a new profile first. */
export const FRESHNESS_ORDER: readonly Freshness[] = [
  'stale',
  'ageing',
  'fresh',
  'never',
];

/** A profile younger than this is fresh. A design proposal. */
export const FRESH_WITHIN_HOURS = 24;
/** A profile younger than this, and not fresh, is ageing; anything older is stale. A design proposal. */
export const AGEING_WITHIN_HOURS = 7 * 24;

const HOUR_MS = 3_600_000;

/** How old a table's profile is. A table missing from the facts map was never profiled — the facts
 * route omits it rather than sending nulls, and that absence is the answer. */
export function freshnessOf(facts: DatasetFacts | undefined, now: Date): Freshness {
  if (facts?.last_profiled_at === undefined || facts.last_profiled_at === null)
    return 'never';
  const at = new Date(facts.last_profiled_at).getTime();
  if (Number.isNaN(at)) return 'never';
  const hours = (now.getTime() - at) / HOUR_MS;
  if (hours < FRESH_WITHIN_HOURS) return 'fresh';
  if (hours < AGEING_WITHIN_HOURS) return 'ageing';
  return 'stale';
}

/* ------------------------------------------------------------------ tones */

/** The four tones the screen draws a figure in. `none` is "not measured", never a low value. */
export type Tone = 'good' | 'watch' | 'risk' | 'none';

/** Completeness at or above this is good. The product's watch line. */
export const COMPLETENESS_GOOD = 0.9;
/** Completeness below this is at risk. A design proposal. */
export const COMPLETENESS_RISK = 0.7;

export function completenessTone(value: number | null): Tone {
  if (value === null) return 'none';
  if (value >= COMPLETENESS_GOOD) return 'good';
  if (value >= COMPLETENESS_RISK) return 'watch';
  return 'risk';
}

/** The baseline's bands, the same 75/90 the dataset page uses. */
export const BASELINE_HEALTHY = 90;
export const BASELINE_WATCH = 75;

export function baselineTone(value: number | null): Tone {
  if (value === null) return 'none';
  if (value >= BASELINE_HEALTHY) return 'good';
  if (value >= BASELINE_WATCH) return 'watch';
  return 'risk';
}

/** The baseline's band, in words. */
export type BaselineBand = 'healthy' | 'watch' | 'atRisk';

export function baselineBand(value: number): BaselineBand {
  if (value >= BASELINE_HEALTHY) return 'healthy';
  if (value >= BASELINE_WATCH) return 'watch';
  return 'atRisk';
}

/* ------------------------------------------------------------------ the freshness donut */

export interface DonutSegment {
  readonly freshness: Freshness;
  /** Where the segment starts and ends, as percentages of the ring. */
  readonly from: number;
  readonly to: number;
}

/** The donut's segments in the estate's order, skipping empty ones. Each ends a little early, so a
 * thin gap separates neighbours and a one-table segment still reads as its own. */
export function donutSegments(
  counts: Readonly<Record<Freshness, number>>,
): DonutSegment[] {
  const total = FRESHNESS_ORDER.reduce((sum, key) => sum + counts[key], 0);
  if (total === 0) return [];
  const segments: DonutSegment[] = [];
  let start = 0;
  for (const freshness of FRESHNESS_ORDER) {
    const share = counts[freshness] / total;
    if (share === 0) continue;
    const end = start + share * 100;
    segments.push({ freshness, from: start, to: Math.max(start, end - DONUT_GAP) });
    start = end;
  }
  return segments;
}

/** The gap between donut segments, in percent of the ring. */
const DONUT_GAP = 0.6;

/* ------------------------------------------------------------------ cardinality */

/* The five steps are the profiler's, so the dataset page and this screen agree on what "unique" means. */
export { cardinalityLevel };
export type { CardinalityLevel };

export const CARDINALITY_LABEL_KEYS: Readonly<Record<CardinalityLevel, string>> = {
  0: 'profiles.cardinality.unmeasured',
  1: 'profiles.cardinality.constant',
  2: 'profiles.cardinality.few',
  3: 'profiles.cardinality.many',
  4: 'profiles.cardinality.mostlyUnique',
  5: 'profiles.cardinality.unique',
};

/** A distinct count as the column map prints it: exact with grouping, an estimate marked `≈`. */
export function distinctText(sketch: ColumnSketch): string {
  if (sketch.distinct_exact !== null) return sketch.distinct_exact.toLocaleString();
  return `≈ ${Math.round(sketch.distinct_estimate).toLocaleString()}`;
}

/* ------------------------------------------------------------------ the baseline waterfall */

/** Where the waterfall's axis starts. A finding costs a handful of points, and a 0–100 axis would
 * draw every step as a sliver. */
export const WATERFALL_FLOOR = 50;

export interface WaterfallStep {
  readonly kind: 'start' | 'deduction' | 'baseline';
  /** The figure above the bar: 100, a deduction, or the baseline. */
  readonly figure: number;
  /** The column a deduction is for. */
  readonly column?: string;
  readonly finding?: BaselineFinding['kind'];
  /** The bar's bottom and height, as percentages of the chart's height. */
  readonly bottom: number;
  readonly height: number;
  readonly tone: Tone | 'start';
}

function heightOf(value: number): number {
  const height = ((value - WATERFALL_FLOOR) / (100 - WATERFALL_FLOOR)) * 100;
  return Math.max(0, Math.min(100, height));
}

/** From 100, one step down per finding, to the baseline. `null` when there is no baseline to reach. */
export function waterfall(baseline: ProfileBaseline): WaterfallStep[] | null {
  if (baseline.value === null) return null;
  const steps: WaterfallStep[] = [
    { kind: 'start', figure: 100, bottom: 0, height: 100, tone: 'start' },
  ];
  let current = 100;
  for (const finding of baseline.findings) {
    const next = current - finding.deduction;
    steps.push({
      kind: 'deduction',
      figure: finding.deduction,
      column: finding.column,
      finding: finding.kind,
      bottom: heightOf(next),
      height: heightOf(current) - heightOf(next),
      tone: 'risk',
    });
    current = next;
  }
  steps.push({
    kind: 'baseline',
    figure: baseline.value,
    bottom: 0,
    height: heightOf(baseline.value),
    tone: baselineTone(baseline.value),
  });
  return steps;
}

/* ------------------------------------------------------------------ what changed */

/** A completeness movement worth drawing as a slope, and the two points to draw it between. */
export interface CompletenessSlope {
  readonly before: number;
  readonly after: number;
  readonly low: number;
  readonly high: number;
  readonly fell: boolean;
}

export function completenessSlope(drift: ColumnDrift): CompletenessSlope | null {
  const before = drift.completeness_before;
  const after = drift.completeness_after;
  if (before === null || after === null || Math.abs(after - before) < 0.001) return null;
  return {
    before,
    after,
    low: Math.min(before, after),
    high: Math.max(before, after),
    fell: after < before,
  };
}

/** A completeness movement in points, or `null` when it is too small to mention or not measurable. */
export function completenessDelta(
  before: number | null,
  after: number | null,
): number | null {
  if (before === null || after === null) return null;
  const delta = (after - before) * 100;
  return Math.abs(delta) < 0.05 ? null : delta;
}

/* ------------------------------------------------------------------ the estate map */

/** The map's age axis: half an hour to sixty days on a log scale, so a table profiled this morning and
 * one profiled last month both have room. Anything older is drawn at the right edge. */
export const MAP_AGE_MIN_HOURS = 0.5;
export const MAP_AGE_MAX_HOURS = 1440;

/** How far along the age axis a profile this many hours old sits, as a percentage of its width. */
export function ageAxis(hours: number): number {
  const position =
    (Math.log(Math.max(MAP_AGE_MIN_HOURS, hours) / MAP_AGE_MIN_HOURS) /
      Math.log(MAP_AGE_MAX_HOURS / MAP_AGE_MIN_HOURS)) *
    100;
  return Math.max(0, Math.min(100, position));
}

/** How high a baseline sits on the map, as a percentage of its height. The axis starts at the
 * waterfall's floor for the same reason: baselines cluster high. */
export function baselineAxis(value: number): number {
  return heightOf(value);
}

export interface AgeTick {
  readonly hours: number;
  readonly amount: number;
  readonly unit: 'hour' | 'day';
}

/** The ticks under the map's age axis. */
export const MAP_AGE_TICKS: readonly AgeTick[] = [
  { hours: 1, amount: 1, unit: 'hour' },
  { hours: 6, amount: 6, unit: 'hour' },
  { hours: 24, amount: 24, unit: 'hour' },
  { hours: 72, amount: 3, unit: 'day' },
  { hours: 168, amount: 7, unit: 'day' },
  { hours: 720, amount: 30, unit: 'day' },
];

/** Hours since an instant, or `null` when it does not parse. */
export function hoursSince(iso: string, now: Date): number | null {
  const at = new Date(iso).getTime();
  return Number.isNaN(at) ? null : (now.getTime() - at) / HOUR_MS;
}

/** A bubble's diameter in pixels. Its area rather than its width follows the rows scanned, so the
 * largest table does not dwarf every other. */
export function bubbleSize(rows: number, largest: number): number {
  return Math.round(12 + 26 * Math.sqrt(Math.max(0, rows) / Math.max(1, largest)));
}

/* ------------------------------------------------------------------ findings */

/** The finding kinds in the order the baseline weighs them, most severe first. */
export const FINDING_KINDS: readonly BaselineFinding['kind'][] = [
  'all_null',
  'mostly_null',
  'sparse',
  'constant',
  'dominated',
];

/* ------------------------------------------------------------------ column shape */

/** The shape chart's vertical axis starts at one distinct value in a hundred thousand rows. */
const SHARE_FLOOR_LOG = -5;

/** Where a distinct share sits on the shape chart's log axis, as a percentage of its height. */
export function shareAxis(share: number): number {
  return ((Math.log10(share) - SHARE_FLOOR_LOG) / -SHARE_FLOOR_LOG) * 100;
}

/** The horizontal rules on the shape chart's vertical axis, as distinct shares. */
export const SHARE_TICKS: readonly number[] = [1e-5, 1e-3, 0.1, 1];

/** How high a column sits on the shape chart: its distinct values as a share of its rows, on a log
 * scale, kept off the frame so a key column's dot is not cut by the top edge. */
export function distinctShareAxis(sketch: ColumnSketch): number {
  const distinct = sketch.distinct_exact ?? sketch.distinct_estimate;
  if (sketch.rows_scanned === 0 || distinct <= 0) return 0;
  const ratio = Math.min(1, distinct / sketch.rows_scanned);
  return Math.max(5, Math.min(96, shareAxis(Math.max(ratio, 1e-5))));
}

/** Columns this close on both axes share one dot and one label, so two identical columns are not
 * drawn on top of each other. */
const GROUP_WITHIN = 2;
/** A label to the right of a dot past this far across would run off the chart, so it goes left. */
const FLIP_PAST = 75;

export interface ShapeGroup {
  readonly columns: readonly string[];
  readonly sketches: readonly ColumnSketch[];
  /** The dot's position, as percentages of the chart's width and height. */
  readonly x: number;
  readonly y: number;
  readonly tone: Tone;
  readonly dominated: boolean;
  /** The label sits left of the dot rather than right of it. */
  readonly flip: boolean;
  /** How many label heights the label moves off its dot to clear the others: up when positive, down
   * when negative, level when 0. */
  readonly lane: number;
}

/* Label geometry, in pixels: the chart is a fixed 260px tall and as wide as its card, and a label is
   10px monospace beside a 12px dot. Measured in pixels because a label's width does not scale with
   the chart, so a percentage estimate is right at one card width and wrong at every other. */
/** The shape chart's height. */
export const SHAPE_HEIGHT = 260;
/** A label lane: the step a label moves to clear another. */
const LANE_PX = 15;
/** Half a label's height. */
const LABEL_HALF_PX = 7;
/** A monospace character at the label's size, and the label's padding. */
const CHAR_PX = 6.1;
const LABEL_PAD_PX = 6;
/** From a dot's centre to its label: its radius, then the label's margin. */
const DOT_TO_LABEL_PX = 14;
/** A dot's reach, including its white ring. */
const DOT_REACH_PX = 9;
/** The lanes a label tries in turn: level, then a step up or down, then two. */
const LANES: readonly number[] = [0, 1, -1, 2, -2];

interface Box {
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
  readonly top: number;
}

function overlaps(a: Box, b: Box): boolean {
  return a.left < b.right && b.left < a.right && a.bottom < b.top && b.bottom < a.top;
}

interface Placed {
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly flip: boolean;
}

/** Each label's lane: the first that keeps it inside the chart and clear of every label placed before
 * it and every other dot. Labels are placed from the top of the chart down, so the crowded top corner,
 * where keys and IDs gather, is settled first. A label that fits nowhere stays level with its dot. */
function lanesFor(groups: readonly Placed[], width: number): number[] {
  const toX = (x: number): number => (x / 100) * width;
  const toY = (y: number): number => (y / 100) * SHAPE_HEIGHT;
  const labelBox = (group: Placed, lane: number): Box => {
    const x = toX(group.x);
    const span = group.label.length * CHAR_PX + LABEL_PAD_PX;
    const middle = toY(group.y) + lane * LANE_PX;
    const near = group.flip ? x - DOT_TO_LABEL_PX : x + DOT_TO_LABEL_PX;
    return {
      left: group.flip ? near - span : near,
      right: group.flip ? near : near + span,
      bottom: middle - LABEL_HALF_PX,
      top: middle + LABEL_HALF_PX,
    };
  };
  const inside = (box: Box): boolean => box.bottom >= 0 && box.top <= SHAPE_HEIGHT;
  const lanes = groups.map(() => 0);
  const placed: Box[] = [];
  const order = groups
    .map((group, index) => ({ group, index }))
    .sort((a, b) => b.group.y - a.group.y || a.group.x - b.group.x);
  for (const { group, index } of order) {
    const dots = groups
      .filter((_, other) => other !== index)
      .map((other) => ({
        left: toX(other.x) - DOT_REACH_PX,
        right: toX(other.x) + DOT_REACH_PX,
        bottom: toY(other.y) - DOT_REACH_PX,
        top: toY(other.y) + DOT_REACH_PX,
      }));
    const lane =
      LANES.find((each) => {
        const box = labelBox(group, each);
        return (
          inside(box) &&
          !placed.some((other) => overlaps(box, other)) &&
          !dots.some((dot) => overlaps(box, dot))
        );
      }) ?? 0;
    lanes[index] = lane;
    placed.push(labelBox(group, lane));
  }
  return lanes;
}

/** The shape chart's dots, and how many columns could not be plotted because nothing was measured. */
export function shapeGroups(
  columns: readonly ColumnSketch[],
  isDominated: (sketch: ColumnSketch) => boolean,
  /** The chart's rendered width in pixels, which decides where each label fits. */
  width = 520,
): { readonly groups: ShapeGroup[]; readonly unplotted: number } {
  const measured = columns.filter(
    (sketch) => sketch.completeness !== null && sketch.rows_scanned > 0,
  );
  const gathered: { x: number; y: number; members: ColumnSketch[] }[] = [];
  for (const sketch of measured) {
    const x = (sketch.completeness ?? 0) * 100;
    const y = distinctShareAxis(sketch);
    const near = gathered.find(
      (group) =>
        Math.abs(group.x - x) < GROUP_WITHIN && Math.abs(group.y - y) < GROUP_WITHIN,
    );
    if (near === undefined) gathered.push({ x, y, members: [sketch] });
    else near.members.push(sketch);
  }
  const labelled = gathered.map((group) => ({
    ...group,
    flip: group.x > FLIP_PAST,
    label: group.members.map((sketch) => sketch.column).join(', '),
  }));
  const lanes = lanesFor(labelled, width);
  const groups = labelled.map((group, index) => {
    const first = group.members[0];
    return {
      columns: group.members.map((sketch) => sketch.column),
      sketches: group.members,
      x: group.x,
      y: group.y,
      tone: completenessTone(first?.completeness ?? null),
      dominated: group.members.some(isDominated),
      flip: group.flip,
      lane: lanes[index] ?? 0,
    };
  });
  return { groups, unplotted: columns.length - measured.length };
}

/* ------------------------------------------------------------------ completeness, previous and now */

export interface DumbbellRow {
  readonly column: string;
  readonly before: number;
  readonly after: number;
  /** The two dots and the segment between them, as percentages of the track's width. */
  readonly beforeAt: number;
  readonly afterAt: number;
  readonly from: number;
  readonly width: number;
  readonly direction: 'down' | 'up' | 'flat';
  /** The movement in points, always positive; `direction` says which way. */
  readonly points: number;
}

export interface Dumbbell {
  readonly rows: DumbbellRow[];
  /** The track's three ticks, as completeness fractions: its floor, its middle, and 100%. */
  readonly ticks: readonly [number, number, number];
  /** Where a completeness sits on the track, as a percentage of its width. */
  readonly at: (value: number) => number;
}

/** A movement smaller than this is drawn grey: a twentieth of a point is rounding, not change. */
const DUMBBELL_STILL = 0.0005;

/** Each compared column's completeness at the previous profile and now, biggest fall first. The track
 * starts a little below the lowest of them, so small movements are not squeezed into the right edge.
 * `null` when no column was measured on both sides. */
export function dumbbell(
  columns: readonly ColumnSketch[],
  drift: readonly ColumnDrift[] | null,
): Dumbbell | null {
  const earlier = new Map(
    (drift ?? []).map((each) => [each.column, each.completeness_before]),
  );
  const pairs = columns.flatMap((sketch) => {
    const before = earlier.get(sketch.column) ?? null;
    const after = sketch.completeness;
    return before === null || after === null
      ? []
      : [{ column: sketch.column, before, after }];
  });
  if (pairs.length === 0) return null;
  const lowest = Math.min(...pairs.map((pair) => Math.min(pair.before, pair.after)));
  const floor = Math.max(0, Math.floor(lowest * 10) / 10 - 0.1);
  const range = 1 - floor || 1;
  const at = (value: number): number => ((value - floor) / range) * 100;
  const rows = pairs
    .sort((a, b) => a.after - a.before - (b.after - b.before))
    .map((pair): DumbbellRow => {
      const direction =
        pair.after < pair.before - DUMBBELL_STILL
          ? 'down'
          : pair.after > pair.before + DUMBBELL_STILL
            ? 'up'
            : 'flat';
      return {
        ...pair,
        beforeAt: at(pair.before),
        afterAt: at(pair.after),
        from: at(Math.min(pair.before, pair.after)),
        width: (Math.abs(pair.after - pair.before) / range) * 100,
        direction,
        points: Math.abs(pair.after - pair.before) * 100,
      };
    });
  return { rows, ticks: [floor, floor + (1 - floor) / 2, 1], at };
}
